<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'next/switch/Switch.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const restrictAgentVisibility = ref(true);
const forceAgentOfflineControl = ref(false);
const preventConversationReopen = ref(false);
const isSubmitting = ref(false);
const initialized = ref(false);

watch(
  currentAccount,
  () => {
    if (isSubmitting.value) return;
    const account = currentAccount.value || {};
    restrictAgentVisibility.value =
      account.restrict_agent_conversation_visibility ?? true;
    forceAgentOfflineControl.value =
      account.force_agent_offline_control ?? false;
    preventConversationReopen.value =
      account.prevent_conversation_reopen ?? false;
    initialized.value = true;
  },
  { deep: true, immediate: true }
);

const updateSetting = async (field, value) => {
  try {
    isSubmitting.value = true;
    await updateAccount({ [field]: value }, { silent: true });
    useAlert(
      t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.API.SUCCESS')
    );
  } catch (error) {
    useAlert(
      t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.API.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const toggleRestrictAgentVisibility = () => {
  updateSetting(
    'restrict_agent_conversation_visibility',
    restrictAgentVisibility.value
  );
};

const toggleForceAgentOfflineControl = () => {
  updateSetting(
    'force_agent_offline_control',
    forceAgentOfflineControl.value
  );
};

const togglePreventConversationReopen = () => {
  updateSetting(
    'prevent_conversation_reopen',
    preventConversationReopen.value
  );
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.NOTE')"
    with-border
  >
    <div class="grid gap-4">
      <WithLabel
        :label="t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.RESTRICT_AGENT_VISIBILITY.LABEL')"
        :help-message="t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.RESTRICT_AGENT_VISIBILITY.HELP')"
      >
        <Switch
          v-model="restrictAgentVisibility"
          @change="toggleRestrictAgentVisibility"
        />
      </WithLabel>

      <WithLabel
        :label="t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.FORCE_AGENT_OFFLINE.LABEL')"
        :help-message="t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.FORCE_AGENT_OFFLINE.HELP')"
      >
        <Switch
          v-model="forceAgentOfflineControl"
          @change="toggleForceAgentOfflineControl"
        />
      </WithLabel>

      <WithLabel
        :label="t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.PREVENT_REOPEN.LABEL')"
        :help-message="t('GENERAL_SETTINGS.FORM.CONVERSATION_CONTROLS.PREVENT_REOPEN.HELP')"
      >
        <Switch
          v-model="preventConversationReopen"
          @change="togglePreventConversationReopen"
        />
      </WithLabel>
    </div>
  </SectionLayout>
</template>
